import {
    PortfolioPlData,
    PortfolioPlDataItem,
} from 'widgets/portfolioPl/portfolioPlData';
import {
    BucketedDataItem,
    createEmptyBuckets,
    findNextBucket,
} from 'utils/timeseries/bucket';
import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange';
import { getMinutes } from 'utils/timeseries/timeSeriesUtils';
import { timeUtils } from '@instinet-analytics/core-lib';

const { mapEpochToTime, mapTimeToEpoch } = timeUtils;

type PortfolioPlLeavesBucket = BucketedDataItem & {
    item?: PortfolioPlDataItem;
};

export interface PortfolioPlLeavesTimeSeriesItem {
    time: number;
    net?: number;
    buy?: number;
    sell?: number;
}

export function getBucketedTimeSeries(
    data: PortfolioPlData,
    plotAreaWidth: number,
    zoom: number
): PortfolioPlLeavesTimeSeriesItem[] {
    const populated = data.timeSeries.filter((d) => d.netLeaves !== undefined);
    if (populated.length === 0) {
        return [];
    }

    const startTime = data.timeDomain[0];
    const endTime = data.timeDomain[1];

    const buckets = calculateBuckets(
        populated,
        data.breaksInTrading,
        plotAreaWidth,
        startTime,
        endTime,
        zoom
    );

    // Create a time series mapping the net for each data point (as its a line)
    const ret: PortfolioPlLeavesTimeSeriesItem[] = data.timeSeries.map((t) => ({
        net: t.netLeaves,
        time: t.time,
    }));

    // Work through the buckets setting the buy and leaves values for the "item" time (which is the item nearest the mid point)
    for (const bucket of buckets) {
        if (bucket.item) {
            const item = ret.find((i) => i.time === bucket.item!.time)!;
            item.buy = bucket.item?.buyLeaves;
            item.sell = bucket.item?.sellLeaves;
        }
    }

    return ret;
}

function calculateBuckets(
    data: PortfolioPlDataItem[],
    breaksInTrading: TimeSeriesDisableRange[],
    plotAreaWidth: number,
    startTime: number,
    endTime: number,
    zoom: number
): PortfolioPlLeavesBucket[] {
    if (data.length < 2) {
        return [];
    }

    // No. of mins displayed on x axis after breaks deducted...
    const displayedMinutes = getMinutes(startTime, endTime, breaksInTrading);
    // No. of displayed mins with data after breaks deducted...
    const dataMinutes =
        getMinutes(
            mapEpochToTime(data[0]._i),
            mapEpochToTime(data[data.length - 1]._i),
            breaksInTrading
        ) / zoom;
    // No. of px populated by data...
    const dataWidthPx = (plotAreaWidth * dataMinutes) / displayedMinutes;
    // No. of pixels per minute of data...
    const pixelsPerMin = dataWidthPx / dataMinutes;
    // Allow a couple of px per line plus some spacing...
    const desiredBucketWidthPx = 5;
    // Calculate desired bucket size in mins
    const desiredBucketWidthMin = desiredBucketWidthPx / pixelsPerMin;
    const bucketSizeInMinutes = Math.ceil(desiredBucketWidthMin);

    if (bucketSizeInMinutes === 1) {
        // If one data item per bucket then no bucketing required...
        return data.map((d) => ({
            midTime: d.time,
            startTime: d.time,
            endTime: d.time,
            item: d,
            endTimeDisplay: mapTimeToEpoch(d.time),
        }));
    }

    const bucketedData = createEmptyBuckets<PortfolioPlLeavesBucket>(
        bucketSizeInMinutes,
        data,
        breaksInTrading
    );

    // Remember the item nearest the mid time in the buckets "item" property...
    let currentBucket: PortfolioPlLeavesBucket | undefined = bucketedData[0];
    for (const d of data) {
        if (!currentBucket) {
            break;
        }
        if (d.time < currentBucket.endTime) {
            if (d.time <= currentBucket.midTime) {
                currentBucket.item = d;
            }
        } else {
            currentBucket = findNextBucket(bucketedData, currentBucket!);
        }
    }

    return bucketedData;
}
